<template>
    <div class="px-4 pt-10 pb-5 mx-auto max-w-7xl lg:pt-16 lg:pb-10">
        <div class="grid grid-cols-1 rounded-lg lg:grid-cols-12">
            <!-- Left Column - Product Information -->
            <div class="grid col-span-7 items-center px-4 py-8 rounded-tl-lg rounded-tr-lg border-r border-l border-gray-300 md:px-8 lg:rounded-tr-none lg:rounded-bl-lg lg:border-r-0 xl:px-16 xl:py-12 border-y">
                <!-- Main Headline -->
                <div>
                    <div class="mb-8 text-center lg:text-left">
                        <h3 class="mb-4 text-2xl md:text-3xl xl:text-32 font-ww-bold" v-html="mainHeading">
                        </h3>
                        <div class="mb-6 text-base leading-6 lg:text-lg font-ww-regular" v-html="description">
                        </div>
                        <div v-if="showCaffeineInfo" class="inline-block px-4 py-2 bg-[#E1EAF4] rounded-xl">
                            <span class="text-lg text-gray-900 font-ww-bold" v-html="caffeineInfo"></span>
                        </div>
                    </div>
                    <!-- Features Grid -->
                    <div class="grid grid-cols-1 gap-x-6 gap-y-8 mb-12 md:grid-cols-12">
                        <div v-for="feature in features" :key="feature.id" :class="feature.colSpan">
                            <div class="flex items-center space-x-2">
                                <h3 class="text-base leading-5 xl:text-lg font-ww-bold">{{ feature.icon }} {{ feature.title }}</h3>
                            </div>
                            <div class="text-base !leading-5 xl:text-lg font-ww-regular" v-html="feature.description">
                          
                            </div>
                        </div>
                    </div>
                    <!-- Certification Icons -->
                    <div class="grid grid-cols-3 gap-y-6 gap-x-8 md:grid-cols-6">
                        <div v-for="cert in certifications" :key="cert.id" class="text-center">
                            <nuxt-img class="mx-auto w-12 sm:w-12 md:w-14" :src="cert.imageSrc" width="56" height="56" format="webp"
                                loading="lazy" sizes="xs:56px sm:56px md:56px lg:56px xl:56px" />
                            <div class="xl:text-sm text-xs font-ww-regular text-[#3B3B3B] !leading-4 text-center mt-2">{{
                                cert.label }}</div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Right Column - Product Image -->
            <div class="flex relative col-span-5 justify-center items-center">
                <nuxt-img class="object-cover w-full h-full xl:max-h-[892px] lg:w-auto sm:w-8/12 lg:rounded-tr-lg lg:rounded-br-lg lg:rounded-bl-none lg:rounded-tl-none rounded-bl-lg rounded-br-lg " 
                    :src="productImageSrc" 
                    :width="productImageWidth"
                    :height="productImageHeight" 
                    format="webp" 
                    loading="lazy" 
                    :sizes="productImageSizes" />
            </div>
        </div>
    </div>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: 'Focus Longer, Feel Better, No Crashes'
  },
  description: {
    type: String,
    default: 'We upgraded coffee by keeping the good stuff, ditching the junk, and adding brain-boosting nutrients to power your day.'
  },
  caffeineInfo: {
    type: String,
    default: '85mg caffeine'
  },
  showCaffeineInfo: {
    type: Boolean,
    default: true
  },
  features: {
    type: Array,
    default: () => [
      {
        id: 1,
        icon: '☕',
        title: 'Smooth, Delicious Taste',
        description: 'A rich, balanced coffee with subtle chocolatey notes—no bitterness, no acidity.',
        colSpan: 'col-span-6'
      },
      {
        id: 2,
        icon: '🍄',
        title: 'Dual-Extracted Mushrooms',
        description: 'For maximum bioavailability, our functional mushrooms are double-extracted to enhance beta-glucans, hericenones, erinacerins',
        colSpan: 'col-span-6'
      },
      {
        id: 3,
        icon: '🔥',
        title: 'Feel IT',
        description: 'Clear, calm, and focused energy. No overstimulation, just pure productivity and mental sharpness.',
        colSpan: 'col-span-6'
      },
      {
        id: 4,
        icon: '🚫',
        title: 'No Fillers, No B.S.',
        description: 'No myceliated oats or cheap starch fillers—just real, potent ingredients for real results.',
        colSpan: 'col-span-6'
      },
      {
        id: 5,
        icon: '🧪',
        title: '3rd-Party Tested for 450+ Toxins',
        description: 'Every batch is rigorously tested for over 450 toxins, including heavy metals, mycotoxins, and mold—ensuring the purest quality in every sip.',
        colSpan: 'col-span-7'
      }
    ]
  },
  certifications: {
    type: Array,
    default: () => [
      {
        id: 1,
        imageSrc: '/assets/images/benefit-1-neuro.svg',
        label: 'Single Origin'
      },
      {
        id: 2,
        imageSrc: '/assets/images/benefit-2-neuro.svg',
        label: '3rd Party Tested'
      },
      {
        id: 3,
        imageSrc: '/assets/images/benefit-3-neuro.svg',
        label: 'Stomach Friendly'
      },
      {
        id: 4,
        imageSrc: '/assets/images/benefit-5-neuro.svg',
        label: 'USDA Certified'
      },
      {
        id: 5,
        imageSrc: '/assets/images/benefit-6-neuro.svg',
        label: 'Hand Selected'
      },
      {
        id: 6,
        imageSrc: '/assets/images/benefit-7-neuro.svg',
        label: 'Shade Grown'
      },
      {
        id: 7,
        imageSrc: '/assets/images/benefit-4-neuro.svg',
        label: 'TRUSTPURE process'
      }
    ]
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-product-new.png'
  },
  productImageWidth: {
    type: Number,
    default: 492
  },
  productImageHeight: {
    type: Number,
    default: 893
  },
  productImageSizes: {
    type: String,
    default: 'xs:283px sm:393px md:720px lg:492px xl:492px'
  }
})

defineBuilderComponent({
    friendlyName: 'Feature Icons With Image',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'Focus Longer, Feel Better, No Crashes'
        },
        {
            name: 'description',
            type: 'richText',
            defaultValue: 'We upgraded coffee by keeping the good stuff, ditching the junk, and adding brain-boosting nutrients to power your day.'
        },
        {
            name: 'caffeineInfo',
            type: 'text',
            defaultValue: '85mg caffeine'
        },
        {
            name: 'showCaffeineInfo',
            type: 'boolean',
            defaultValue: true,
            friendlyName: 'Show Caffeine Info'
        },
        {
            name: 'features',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number',
                    defaultValue: 1
                },
                {
                    name: 'icon',
                    type: 'text',
                    defaultValue: '☕'
                },
                {
                    name: 'title',
                    type: 'text',
                    defaultValue: ''
                },
                {
                    name: 'description',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'colSpan',
                    type: 'text',
                    defaultValue: 'col-span-6'
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    icon: '☕',
                    title: 'Smooth, Delicious Taste',
                    description: 'A rich, balanced coffee with subtle chocolatey notes—no bitterness, no acidity.',
                    colSpan: 'col-span-6'
                },
                {
                    id: 2,
                    icon: '🍄',
                    title: 'Dual-Extracted Mushrooms',
                    description: 'For maximum bioavailability, our functional mushrooms are double-extracted to enhance beta-glucans, hericenones, erinacerins',
                    colSpan: 'col-span-6'
                },
                {
                    id: 3,
                    icon: '🔥',
                    title: 'Feel IT',
                    description: 'Clear, calm, and focused energy. No overstimulation, just pure productivity and mental sharpness.',
                    colSpan: 'col-span-6'
                },
                {
                    id: 4,
                    icon: '🚫',
                    title: 'No Fillers, No B.S.',
                    description: 'No myceliated oats or cheap starch fillers—just real, potent ingredients for real results.',
                    colSpan: 'col-span-6'
                },
                {
                    id: 5,
                    icon: '🧪',
                    title: '3rd-Party Tested for 450+ Toxins',
                    description: 'Every batch is rigorously tested for over 450 toxins, including heavy metals, mycotoxins, and mold—ensuring the purest quality in every sip.',
                    colSpan: 'col-span-7'
                }
            ]
        },
        {
            name: 'certifications',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number',
                    defaultValue: 1
                },
                {
                    name: 'imageSrc',
                    type: 'file',
                    defaultValue: ''
                },
                {
                    name: 'label',
                    type: 'text',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    imageSrc: '/assets/images/benefit-1-neuro.svg',
                    label: 'Single Origin'
                },
                {
                    id: 2,
                    imageSrc: '/assets/images/benefit-2-neuro.svg',
                    label: '3rd Party Tested'
                },
                {
                    id: 3,
                    imageSrc: '/assets/images/benefit-3-neuro.svg',
                    label: 'Stomach Friendly'
                },
                {
                    id: 4,
                    imageSrc: '/assets/images/benefit-5-neuro.svg',
                    label: 'USDA Certified'
                },
                {
                    id: 5,
                    imageSrc: '/assets/images/benefit-6-neuro.svg',
                    label: 'Hand Selected'
                },
                {
                    id: 6,
                    imageSrc: '/assets/images/benefit-7-neuro.svg',
                    label: 'Shade Grown'
                },
                {
                    id: 7,
                    imageSrc: '/assets/images/benefit-4-neuro.svg',
                    label: 'TRUSTPURE process'
                }
            ]
        },
        {
            name: 'productImageSrc',
            type: 'file',
            defaultValue: '/assets/images/mindflow-product-new.png'
        },
        {
            name: 'productImageWidth',
            type: 'number',
            defaultValue: 492
        },
        {
            name: 'productImageHeight',
            type: 'number',
            defaultValue: 893
        },
        {
            name: 'productImageSizes',
            type: 'text',
            defaultValue: 'xs:283px sm:393px md:720px lg:492px xl:492px'
        }
    ]
})
</script>